/**
 * 陪练分数档位 + 结果特效策略(V2 批次C)—— 三个陪练页(wecom 语音 / wecom 文本 /
 * advisor 文本)的唯一档位来源。
 *
 * **阈值以后端 `CoachingDispatchService.grade()` 为准**:A≥90 / B≥80 / C≥60 / D<60。
 * 管理端前端 `EvaluationDetailDialog.tsx` 是 90/80/70/60(多一档 70)—— 那是配色细节
 * 不是档位。这份常量存在的意义就是别让第三处再漂一次。
 *
 * 设计取舍(方案 docs/2026-08-06-coaching-voice-v2-START.md §4):
 * - **不及格不做惩罚性视觉**:门店里手机会被同事看见,红色/破碎/下坠 = 公开处刑。
 *   低分走中性色 + 建设性文案 + 把「再练一次」做成主按钮。
 * - **不及格不做数字滚动**:看数字慢慢爬到 45 分是一种凌迟,直接淡入。
 * - **低置信度不撒花**:撒错花比不撒花伤害大得多。
 */

export type ScoreGrade = "excellent" | "good" | "pass" | "weak";

/** 及格线 —— 与管理端及格率口径(EvaluationTaskDetailDrawer)同源。 */
export const PASS_SCORE = 60;

export const GRADE_THRESHOLDS: { grade: ScoreGrade; min: number }[] = [
  { grade: "excellent", min: 90 },
  { grade: "good", min: 80 },
  { grade: "pass", min: PASS_SCORE },
  { grade: "weak", min: 0 },
];

export function gradeOf(score: number | null | undefined): ScoreGrade | null {
  if (score === null || score === undefined || Number.isNaN(score)) return null;
  return GRADE_THRESHOLDS.find((t) => score >= t.min)?.grade ?? "weak";
}

export interface CelebrationSpec {
  /** 粒子数(0 = 不撒)。低端机会再砍半,见 ScoreResultCard。 */
  particles: number;
  /** 粒子形状:纸片(撒花)还是星点(星光)。 */
  shape: "confetti" | "spark" | "none";
  /** 分数是否从 0 滚上来。 */
  countUp: boolean;
  /** 是否描一个勾(合格档的「过了」信号)。 */
  checkmark: boolean;
  /** 主按钮是不是「再练一次」(只有待加强档是)。 */
  retryPrimary: boolean;
  /** 特效持续时长(ms),到点把粒子从 DOM 摘掉,不留 opacity:0 的僵尸节点。 */
  durationMs: number;
}

const SPECS: Record<ScoreGrade, CelebrationSpec> = {
  excellent: {
    particles: 60,
    shape: "confetti",
    countUp: true,
    checkmark: false,
    retryPrimary: false,
    durationMs: 2400,
  },
  good: {
    particles: 18,
    shape: "spark",
    countUp: true,
    checkmark: false,
    retryPrimary: false,
    durationMs: 1600,
  },
  pass: {
    particles: 0,
    shape: "none",
    countUp: true,
    checkmark: true,
    retryPrimary: false,
    durationMs: 600,
  },
  weak: {
    particles: 0,
    shape: "none",
    countUp: false,
    checkmark: false,
    retryPrimary: true,
    durationMs: 0,
  },
};

/**
 * 档位 → 特效策略。
 *
 * @param lowConfidence 评估置信度低 → 一律降到静态呈现(分数照给,不庆祝)
 * @param reduceMotion  系统「减弱动效」→ 零粒子零滚动,徽章和颜色保留
 */
export function celebrationOf(
  grade: ScoreGrade,
  lowConfidence: boolean,
  reduceMotion = false
): CelebrationSpec {
  const base = SPECS[grade];
  if (lowConfidence || reduceMotion) {
    return { ...base, particles: 0, shape: "none", countUp: false, durationMs: 0 };
  }
  return base;
}
